import React, { useState, useEffect } from "react";
import {
  IoPersonOutline,
  IoMailOutline,
  IoCallOutline,
  IoBusinessOutline,
} from "react-icons/io5";
import { FaTruck } from "react-icons/fa";
import { useAuth } from "../../auth/AuthProvider";
import Api from "../../axiosConfig";
import { toast } from "react-toastify";

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deliveryStats, setDeliveryStats] = useState({
    total: 0,
    active: 0,
  });

  useEffect(() => {
    const fetchDeliveryStats = async () => {
      try {
        const response = await Api.get("/v1/deliveries/my-deliveries");
        if (Array.isArray(response.data)) {
          const allDeliveries = response.data;
          setDeliveryStats({
            total: allDeliveries.length || 0,
            active: allDeliveries.filter(d => 
              ["assigned", "in_transit"].includes(d.status.toLowerCase())
            ).length || 0,
          });
        } else if (response.data?.deliveries) {
          const allDeliveries = response.data.deliveries;
          setDeliveryStats({
            total: allDeliveries.length || 0,
            active: allDeliveries.filter(d => 
              ["assigned", "in_transit"].includes(d.status.toLowerCase())
            ).length || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching delivery stats:", error);
        toast.error("Failed to load delivery statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryStats();
  }, []);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            User not found
          </h2>
          <p className="mt-2 text-gray-500">
            Please log in to view your profile
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your personal information and view your activity
          </p>
        </div>
      </div>

      {/* Profile Overview */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-5">
            <div className="flex-shrink-0">
              <div className="relative">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={`${user.firstName}'s profile`}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-primary-dark/10 flex items-center justify-center">
                    <IoPersonOutline className="h-8 w-8 text-primary-dark" />
                  </div>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{`${user.firstName} ${user.lastName}`}</h2>
              <p className="text-sm text-gray-500">
                Member since {formatDate(user.createdAt)}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2 bg-primary-dark/10">
                <IoMailOutline className="h-5 w-5 text-primary-dark" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-sm text-gray-900">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2 bg-primary-dark/10">
                <IoCallOutline className="h-5 w-5 text-primary-dark" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Phone</p>
                <p className="text-sm text-gray-900">{user.phone}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2 bg-primary-dark/10">
                <IoBusinessOutline className="h-5 w-5 text-primary-dark" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Address</p>
                <p className="text-sm text-gray-900">{user.address}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="rounded-full p-2 bg-primary-dark/10">
                <IoPersonOutline className="h-5 w-5 text-primary-dark" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Account Type
                </p>
                <p className="text-sm text-gray-900 capitalize">
                  {user.accountType}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Overview */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900">
            Activity Overview
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="bg-primary-dark/5 rounded-lg px-6 py-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaTruck className="h-6 w-6 text-primary-dark" />
                </div>
                <div className="ml-5">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Deliveries
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900">
                      {loading ? (
                        <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                      ) : (
                        deliveryStats.total
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-primary-dark/5 rounded-lg px-6 py-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaTruck className="h-6 w-6 text-primary-dark" />
                </div>
                <div className="ml-5">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Deliveries
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900">
                      {loading ? (
                        <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                      ) : (
                        deliveryStats.active
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
